import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHeader,
  TableRow,
} from '@windmill/react-ui';
import { compare } from 'compare-versions';
import { useCallback, useState } from 'react';

import {
  SolutionDTO,
  SolutionVersionDTO,
} from '../../common/api/models/solution';
import { SearchBar } from '../../common/components/SearchBar';
import { RegisterSolutionHero } from './RegisterSolutionHero';
import { SolutionTableRow } from './SolutionTableRow';

interface SolutionTableProps {
  orgSolutions: SolutionDTO[];
  pageSize?: number;
  onRegisterSolutionIntent: () => void;
}

export const SolutionTable: React.FunctionComponent<SolutionTableProps> = ({
  orgSolutions,
  pageSize: resultsPerPage = 5,
  onRegisterSolutionIntent,
}) => {
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');

  const receiveSearchTextUpdate = useCallback((search: string) => {
    setSearchText(search);
  }, []);

  const latestVersions = getLatestVersionsOnly(orgSolutions);
  const filteredRows = getFilteredRowsBySearch(latestVersions, searchText);
  const orderedRows = getOrderedRowsByName(filteredRows);
  const rows = orderedRows.slice(
    (page - 1) * resultsPerPage,
    page * resultsPerPage
  );

  if (!orgSolutions || orgSolutions.length === 0) {
    return (
      <RegisterSolutionHero
        onRegisterSolutionIntent={onRegisterSolutionIntent}
      />
    );
  }

  return (
    <div data-testid="solutions-table">
      <SearchBar
        className="mb-4"
        onChange={receiveSearchTextUpdate}
        placeholder="Search by solution name"
      />
      <TableContainer>
        <Table>
          <TableHeader>
            <TableRow>
              <TableCell>Solution Name</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHeader>

          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell className="p-4">No results</TableCell>
              </TableRow>
            )}
            {rows.map((solution) => {
              return (
                <SolutionTableRow
                  key={solution.solutionId}
                  solution={solution}
                />
              );
            })}
          </TableBody>
        </Table>
        <TableFooter>
          <Pagination
            label="Table navigation"
            onChange={setPage}
            resultsPerPage={resultsPerPage}
            totalResults={filteredRows?.length}
          />
        </TableFooter>
      </TableContainer>
    </div>
  );
};

const getLatestVersionsOnly = (
  originals: SolutionDTO[]
): SolutionVersionDTO[] => {
  return originals.map((original) => {
    return original.versions.sort((a, b) => {
      if (compare(a.version, b.version, '>')) return -1;
      return 1;
    })[0];
  });
};

const getFilteredRowsBySearch = (
  originals: SolutionVersionDTO[],
  search: string
) => {
  if (!search) {
    return originals;
  }

  return originals.filter((solution) =>
    solution.displayName.toLowerCase().includes(search.toLowerCase())
  );
};

const getOrderedRowsByName = (originals: SolutionVersionDTO[]) => {
  return originals.sort((a, b) => (a.displayName < b.displayName ? -1 : 1));
};
